import httpRequest from "@/auth/http-client";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import IServiceQueue, {
  IServiceQueueInput,
  IUserServiceQueue,
} from "@/interfaces/service-queue.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

class ServiceQueueService {
  private baseUrl = "api/v1/CustomerServiceQueue";

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<IServiceQueue>> {
    const paginationQuery = buildPaginationQuery(pagination);
    return await httpRequest.get<PaginatedResponse<IServiceQueue>>(
      `${this.baseUrl}?${paginationQuery}`
    );
  }

  async filter(
    filter: IFilter<IServiceQueue>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IServiceQueue>> {
    const paginationQuery = buildPaginationQuery(pagination);
    return await httpRequest.post<PaginatedResponse<IServiceQueue>>(
      `${this.baseUrl}/filter?${paginationQuery}`,
      {
        filter,
      }
    );
  }

  async getById(serviceQueueId: number): Promise<IServiceQueue> {
    return await httpRequest.get<IServiceQueue>(`${this.baseUrl}/${serviceQueueId}`);
  }

  async create(formData: IServiceQueueInput): Promise<IServiceQueue> {
    return await httpRequest.post<IServiceQueue>(this.baseUrl, formData);
  }

  async update(formData: IServiceQueueInput, serviceQueueId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${serviceQueueId}`, formData);
  }

  async delete(serviceQueueId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${serviceQueueId}`);
  }

  async getAttendantAvailability(): Promise<{ active: boolean }> {
    return await httpRequest.get<{ active: boolean }>(`${this.baseUrl}/attendant-availability`);
  }

  async setAttendantAvailability(active: boolean): Promise<{ active: boolean }> {
    return await httpRequest.put<{ active: boolean }>(`${this.baseUrl}/attendant-availability`, {
      active,
    });
  }

  /**
   * Diferente de getAttendantAvailability, distingue "não está em fila nenhuma" de
   * "está em fila mas desativado" — a tela de atendimento esconde o controle no primeiro caso.
   */
  async getAttendantQueueStatus(): Promise<{
    isInAnyQueue: boolean;
    active: boolean;
    queueCount: number;
  }> {
    const response = await httpRequest.get<Record<string, unknown>>(`${this.baseUrl}/attendant-queue-status`);
    const payload =
      response && typeof response === "object" && "data" in response && response.data && typeof response.data === "object"
        ? (response.data as Record<string, unknown>)
        : response;

    return {
      isInAnyQueue: Boolean(payload?.isInAnyQueue ?? payload?.IsInAnyQueue),
      active: Boolean(payload?.active ?? payload?.Active),
      queueCount: Number(payload?.queueCount ?? payload?.QueueCount ?? 0) || 0,
    };
  }

  async getOpenConversationsCount(): Promise<{ count: number }> {
    return await httpRequest.get<{ count: number }>(`${this.baseUrl}/open-conversations-count`);
  }

  async getByUser(userId: number): Promise<IUserServiceQueue[]> {
    return await httpRequest.get<IUserServiceQueue[]>(`${this.baseUrl}/by-user/${userId}`);
  }
}

const serviceQueueService = new ServiceQueueService();
export default serviceQueueService;
